import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Optional,
  Output,
  PLATFORM_ID,
  Self,
  ViewChild,
} from '@angular/core';
import {ComboBoxDropdownComponent} from '../combo-box-dropdown/combo-box-dropdown.component';
import {NgbDropdown, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {AbstractControl, ControlValueAccessor, NgControl, NgModel} from '@angular/forms';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'ds-combo-box',
  templateUrl: './combo-box.component.html',
  styleUrls: ['./combo-box.component.scss'],
})
export class ComboBoxComponent
  implements ControlValueAccessor, AfterContentInit, AfterViewInit {
  @ContentChild(ComboBoxDropdownComponent, {static: true})
  comboBoxGroup: ComboBoxDropdownComponent;

  @ViewChild(NgbDropdown, {static: true}) dropdown: NgbDropdown;
  @ViewChild(NgbDropdownToggle, {static: true})
  dropdownToggle: NgbDropdownToggle;

  @ViewChild('comboBoxElement', {static: true}) comboBoxElement: ElementRef;

  @Input() multiselect = false;
  /** Text to display as the selected element. Its an alternative to the selectedHtml. */
  @Input() label = '';
  /** HTML to display as the selected element. Its an alternative to the label. */
  @Input() selectedHtml: string;
  /** If the selected element is multiline */
  @Input() multiline: boolean;
  /** Object that contains an error message and an Icon class for error */
  @Input() error = {message: '', iconClass: ''};
  /** When showErrorMessages is set to false, showError() will always return false */
  @Input() showErrorMessages = true;

  @Input() set disabled(disabled: boolean) {
    this.isDisabled = disabled;
  }

  /** @deprecated use ngModel instead. This will be removed in future versions */
  @Output() valueChange = new EventEmitter<string[]>();

  private onTouchedCallback: () => {};
  private onChangeCallback: (_: any) => {};

  formControl: AbstractControl;
  isDisabled = false;
  tabIndexDropdownBtn = 0;

  /**
   * This component needs a value accessor reference to validate the input with angular validators.
   * Value accessor is not set using the providers to avoid running into a circular import.
   *
   * Further information:
   * https://material.angular.io/guide/creating-a-custom-form-field-control
   * https://stackoverflow.com/questions/44201298
   * https://stackoverflow.com/questions/57186593
   *
   * Please note that for when you use ngModel max and min validators a custom validator should be implemented.
   *
   * @param ngControl used when FormControl is applied to this input
   * @param ngModel used when ngModel is applied to this input
   * @param cdRef change detector reference
   * @param platformId angular platformId
   */
  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() @Self() public ngModel: NgModel,
    private cdRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) public platformId: string,
  ) {
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    if (ngModel) {
      ngModel.valueAccessor = this;
    }
  }

  /**
   * Decides if an error message should be shown for this input field.
   *
   * @returns true if an error message should be shown.
   */
  public get showError(): boolean {
    if (this.showErrorMessages) {
      return this.invalid;
    }
    return false;
  }

  /**
   * Decides if this input field is valid according to validation attributes.
   *
   * @returns false if the content of this input is valid otherwise true.
   */
  get invalid(): boolean {
    return this.formControl ? this.formControl.invalid : false;
  }

  /**
   * Setting the value accessor directly (instead of using the providers)
   * to avoid running into a circular import.
   *
   * @param ngControl used when formcontrol is applied to this input
   * @param ngModel used when ngModel is applied to this input
   */
  setFormControl(ngControl: NgControl, ngModel: NgModel) {
    if (ngModel && ngModel.control) {
      this.formControl = ngModel.control;
    } else if (ngControl && ngControl.control) {
      this.formControl = ngControl.control;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.isDisabled) {
      this.tabIndexDropdownBtn = -1;
    } else {
      this.tabIndexDropdownBtn = 0;
    }
  }

  get value(): string[] {
    if (this.comboBoxGroup) {
      return this.comboBoxGroup.value;
    }
    return [];
  }

  /**
   * set accessor including call the onchange callback
   */
  set value(v: string[]) {
    if (v !== this.value) {
      this.comboBoxGroup.writeValue(v);
      this.changeComboBoxValue(v);
    }
  }

  changeComboBoxValue(v: string[]) {
    if (this.onChangeCallback) {
      this.onChangeCallback(v);
    }
  }

  get labelList(): string[] {
    if (this.comboBoxGroup && this.comboBoxGroup.comboBoxDropdownGroup) {
      return this.comboBoxGroup.comboBoxDropdownGroup.listItems
        .filter((item) => !!this.value.find((val) => item.value === val))
        .map((item) => item.getLabel());
    }
    return [];
  }

  labelTextWidth(text: string, font: string): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 0;
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }

  blueTagLabel(labelArray: string[]): string {
    return '... +' + (this.labelList.length - labelArray.length);
  }

  get visibleLabelList(): string[] {
    const {offsetWidth} = this.comboBoxElement.nativeElement;
    const fontSpec = '16px BentonSansBook';
    let removeButtonWidth = 12.3;
    let paddingAndMargin = 24;
    if (screen.width < 768) {
      paddingAndMargin *= 0.7;
      removeButtonWidth = 13.3;
    }
    const visibleLabels = [];
    let badgesWidth = 0;
    const childrenWidth = this.labelList.reduce(
      (acc, label) => {
        const badgeWidth =
          this.labelTextWidth(label, fontSpec) + paddingAndMargin + removeButtonWidth;
        return acc + badgeWidth;
      },
      0
    );
    if (childrenWidth < offsetWidth) {
      return null;
    } else {
      this.labelList.forEach(label => {
        const badgeWidth =
          this.labelTextWidth(label, fontSpec) + paddingAndMargin + removeButtonWidth;
        const blueBadgeWidth = this.labelTextWidth(this.blueTagLabel(visibleLabels), fontSpec) + paddingAndMargin;
        badgesWidth += badgeWidth;
        if (offsetWidth > badgesWidth + blueBadgeWidth) {
          visibleLabels.push(label);
        } else {
          badgesWidth -= badgeWidth;
        }
      });
      return visibleLabels;
    }
  }

  /**
   * Reacts on [(ngModel)] changes.
   *
   * @param value value passed into [(ngModel)]
   */
  writeValue(value: string[]) {
    this.value = value;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  keydownOnWrapper(event: KeyboardEvent) {
    if (event.code === 'Enter') {
      this.dropdown.open();
    } else if (event.code === 'Escape') {
      setTimeout(() => {
        this.elementRef.nativeElement.querySelector('.dropdown-toggle').focus();
      });
      this.dropdown.close();
    }
  }

  public focus(): void {
    this.dropdownToggle.nativeElement.focus();
  }

  ngAfterContentInit(): void {
    if (this.comboBoxGroup && this.comboBoxGroup.valueChange) {
      this.comboBoxGroup.valueChange
        .pipe(untilDestroyed(this))
        .subscribe((value: string[]) => {
          this.changeComboBoxValue(value);
          this.valueChange.emit(value);
          if (!this.multiselect) {
            this.dropdown.close();
          }
        });
    }
  }

  ngAfterViewInit(): void {
    this.dropdown.openChange
      .pipe(untilDestroyed(this))
      .subscribe((didOpen: boolean) => {
        if (didOpen) {
          this.comboBoxGroup.setFocusState(true);
          this.comboBoxGroup.setFocusState(true);
        } else {
          this.comboBoxGroup.resetSearch();
        }
      });
    this.setFormControl(this.ngControl, this.ngModel);
    this.cdRef.detectChanges();
  }

  removeLabelEntry(event: MouseEvent, labelEntry: string): void {
    event.stopPropagation();
    this.comboBoxGroup.comboBoxDropdownGroup.listItems.forEach((el) => {
      if (el.label === labelEntry) {
        el.isActive = false;
        this.valueChange.emit(this.value);
      }
    });
    this.changeComboBoxValue(this.value);
  }

  onDropdownToggle(open: boolean) {
    if (open) {
      // timeout is needed as scrollHeight and clientHeight must be calculated after options have been rendered
      setTimeout(() => {
        const dropdownGroup = this.elementRef.nativeElement.querySelector('ds-combo-box-dropdown-group .list-group');
        const scrollVisible = dropdownGroup.scrollHeight > dropdownGroup.clientHeight;
        if (scrollVisible) {
          this.elementRef.nativeElement.querySelector('ds-combo-box-dropdown-group').classList.add('scrollable');
        } else {
          this.elementRef.nativeElement.querySelector('ds-combo-box-dropdown-group').classList.remove('scrollable');
        }
      });
    }
  }
}
